import { FileOutput } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Label } from '~/components/ui/label';
import { Skeleton } from '~/components/ui/skeleton';
import { joinPath } from '~/features/integrations/utils/remote-paths';

import type { FileLayoutState } from '../../hooks/use-file-layout';

interface Props {
  layout: FileLayoutState;
  /** The connection's remote directory (its OUTPUT target path). */
  directory: string;
  /** The host dialog's "Delivered to" label, so both paths read the same. */
  label: string;
  /** E2E `data-testid` root; paths derive `${testId}-...`. */
  testId?: string;
}

/**
 * "Delivered to" for a delivery a file transformer builds: every file it writes
 * (one per assigned transformer, or the one a Lily user picked), joined to the
 * connection's directory. Names come from resolve, rendered now; a transformer's
 * subfolder isn't in resolve, so the path is marked approximate.
 */
export function LayoutDeliveredTo({
  layout,
  directory,
  label,
  testId = 'layout-delivered-to',
}: Props) {
  const { t } = useTranslation('transformers');
  return (
    <div data-testid={testId} className="space-y-1.5">
      <Label className="text-sm">{label}</Label>
      {layout.fileNamesLoading ? (
        <Skeleton className="h-9 w-full" />
      ) : layout.fileNames.length === 0 ? (
        <p data-testid={`${testId}-unknown`} className="text-muted-foreground text-xs">
          {t('layout.namesUnknown')}
        </p>
      ) : (
        <ul data-testid={`${testId}-list`} className="flex flex-col gap-1">
          {layout.fileNames.map((name) => (
            <li
              key={name}
              data-testid={`${testId}-item-${name}`}
              className="bg-muted/40 flex items-center gap-2 rounded-md border px-3 py-2 font-mono text-xs break-all"
            >
              <FileOutput aria-hidden="true" className="text-muted-foreground h-4 w-4 shrink-0" />
              {joinPath(directory || '/', name)}
            </li>
          ))}
        </ul>
      )}
      <p className="text-muted-foreground text-xs">{t('layout.approximatePaths')}</p>
    </div>
  );
}
